import React, { useEffect } from 'react'
import { useAuthState, useQuery } from 'packages/core'
import { Loader } from 'packages/eid-ui'
import {
    useAccessSubcomponents,
    useTranslations,
    useCurrentPerson,
    usePerson,
} from 'hooks'
import moment from 'moment'
import i18n from 'i18next'
import { useAppState } from 'appContext'

const useLoadAccessSubcomponents = () => {
    const [{ access }, dispatchAuthState] = useAuthState()
    const { data } = useAccessSubcomponents()

    useEffect(() => {
        if (data) {
            dispatchAuthState({
                type: 'SET_ACCESS_SUBCOMPONENTS',
                payload: data.data.map((item) => {
                    return {
                        ...item,
                        name: item.applicationName,
                        type: 'Control',
                    }
                }),
            })
        }
    }, [data, dispatchAuthState])

    return access !== null
}

const useLoadTranslations = () => {
    const RESOURCE_SET_NAME = 'RTA'

    const query = useQuery()
    // eslint-disable-next-line
    const [{ debugLocalization: isLocalized }, dispatchAppState] = useAppState()

    const debugLocalization =
        query.get('debugLocalization') === 'true' || isLocalized

    const { data } = useTranslations()
    const { data: currentPerson } = useCurrentPerson()

    const locale =
        currentPerson && currentPerson.language?.languageCode
            ? currentPerson.language?.languageCode
            : 'en'
    useEffect(() => {
        i18n.changeLanguage(locale)
        moment.locale(locale)
    }, [locale])

    useEffect(() => {
        if (query.get('debugLocalization') === 'true') {
            dispatchAppState({
                type: 'SET_DEBUG_LOCALE',
                payload: true,
            })
        }
    }, [])

    useEffect(() => {
        if (data) {
            const localeData = data.reduce((current, r) => {
                let keyToUse = r.key
                const parts = r.key.split('_')
                if (parts[0] === RESOURCE_SET_NAME) {
                    keyToUse = r.key.substring(RESOURCE_SET_NAME.length + 1)
                }

                if (debugLocalization) {
                    current[keyToUse] = r.key
                } else {
                    // If translatedValue is missing/empty, don't add it to the bundle
                    // This allows i18next's fallback mechanism to return the key
                    if (
                        r.translatedValue &&
                        r.translatedValue.trim() !== '' &&
                        r.translatedValue !== null &&
                        r.translatedValue !== undefined
                    ) {
                        current[keyToUse] = r.translatedValue
                    }
                    // If translation is missing, i18next will automatically return the key
                }

                return current
            }, {})

            i18n.addResourceBundle(locale, 'translation', localeData)
        }
    }, [data, locale, debugLocalization])

    return Boolean(data)
}

const withRequiredData = (ChildComponent) => (props) => {
    const translationsLoaded = useLoadTranslations()
    const accessSubcomponentsLoaded = useLoadAccessSubcomponents()

    if (!accessSubcomponentsLoaded || !translationsLoaded) {
        return <Loader />
    } else {
        return <ChildComponent {...props} />
    }
}

export default withRequiredData
